<i18n>
  {
    "fr": {
      "breadcrumb": "Comptes utilisateurs",
      "title": "Recherche de comptes",
      "email-label": "Courriel",
      "email-placeholder": "courriel@exemple.com",
      "search-label": "Rechercher",
      "search-results-empty": "Aucun résultat pour cette recherche",
      "search-results-prompt": "Saisissez le courriel du compte que vous recherchez",
      "user-name-label": "Utilisateur",
      "user-email-label": "Courriel",
      "user-last-login-label": "Dernière connexion",
      "user-business-label": "Entreprises",
      "unconfirmed-email": "Compte non confirmé",
      "none-label": "Aucune",
      "change-email-tooltip" : "Modifier l'adresse courriel",
      "change-user-email-success": "Un courriel de confirmation a été envoyé à la nouvelle adresse de l'utilisateur",
      "error-email-already-assigned" : "Cette adresse courriel est déjà assignée à un compte",
      "error-email-change-failed": "Une erreur est survenue. L'adresse courriel n'a pu être modifiée"
    },
    "en": {
      "breadcrumb": "User accounts",
      "title": "Accounts search",
      "email-label": "Email",
      "email-placeholder": "email@example.com",
      "search-label": "Search",
      "search-results-empty": "No results for this search query",
      "search-results-prompt": "Enter the email of the account you are looking for...",
      "user-name-label": "User",
      "user-email-label": "Email",
      "user-last-login-label": "Last login",
      "user-business-label": "Businesses",
      "unconfirmed-email": "Unconfirmed account",
      "none-label": "None",
      "change-email-tooltip" : "Change email address",
      "change-user-email-success": "A confirmation email has been sent to the user's new address",
      "error-email-already-assigned" : "This email address is already assigned to an account",
      "error-email-change-failed": "An error occurred. The email address could not be changed"
    }
  }
  </i18n>
<template>
  <div>
    <h1 class="title is-2 has-text-weight-bold">{{ $t('title') }}</h1>

    <s-form ref="form" @submit="onSubmit" class="content">
      <b-field class="has-max-width-768" expanded :value="searchModel.email">
        <template #label>
          <span class="sr-only">{{ $t('email-label') }}</span>
        </template>
        <b-input v-model="searchModel.email" :placeholder="$t('email-placeholder')" />
        <b-button native-type="submit" type="is-primary" :loading="loading">
          {{ $t('search-label') }}
        </b-button>
      </b-field>
    </s-form>

    <b-notification v-if="searchResults === null" type="is-light" :closable="false">
      {{ $t('search-results-prompt') }}
    </b-notification>
    <div v-else class="card px-4">
      <s-loader :active="loading" />
      <b-table
        :data="users"
        :loading="loading"
        :current-page="searchModel.currentPage"
        :per-page="searchModel.perPage"
        :total="totalCount"
        :paginated="isPaginated"
        @page-change="onPageChange"
        backend-pagination
      >
        <template #empty>
          <b-notification type="is-light" :closable="false">
            <div class="has-text-centered">{{ $t('search-results-empty') }}</div>
          </b-notification>
        </template>
        <b-table-column v-slot="props" :label="$t('user-name-label')">
          {{ props.row.profile.firstName }} {{ props.row.profile.lastName }}
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('user-email-label')">
          {{ props.row.email }}
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('user-business-label')">
          <b-taglist v-if="!!props.row.business.length">
            <b-tag v-for="business in props.row.business" :key="business.id">
              {{ business.name }}
            </b-tag>
          </b-taglist>
          <span v-else class="is-italic">{{ $t('none-label') }}</span>
        </b-table-column>
        <b-table-column field="lastLogin" sortable :label="$t('user-last-login-label')">
          <template v-slot="props">
            <span v-if="!!props.row.emailConfirmed">
              {{ !!props.row.lastLogin ? $format.dateTime(props.row.lastLogin) : '' }}
            </span>
            <span v-else class="is-italic">{{ $t('unconfirmed-email') }}</span>
          </template>
        </b-table-column>

        <b-table-column v-slot="{ row }">
          <b-field position="is-right">
            <p class="control">
              <b-tooltip :label="$t('change-email-tooltip')" type="is-light" size="is-small" position="is-left">
                <b-button outlined size="is-small" @click="openChangeEmail(row)">
                  <span class="icon is-small has-text-link"><i class="fas fa-pen" /></span>
                </b-button>
              </b-tooltip>
            </p>
          </b-field>
        </b-table-column>
      </b-table>
    </div>
    <change-email ref="changeEmail" admin-mode :loading="loading" @change-email="changeEmail" />
  </div>
</template>

<script>
import Breadcrumbs from '@/mixins/breadcrumbs'
import SForm from '@/components/form/form'
import ChangeEmail from '@/components/businesses/change-email'

export default {
  mixins: [Breadcrumbs],
  components: { SForm, ChangeEmail },
  data() {
    return {
      loading: false,
      searchModel: {
        email: '',
        currentPage: 1,
        perPage: 25
      },
      searchResults: null,
      editingUserId: null
    }
  },
  computed: {
    breadcrumbs() {
      return [{ label: this.$t('breadcrumb') }]
    },
    users() {
      if (!this.searchResults) return []
      return this.searchResults.items || []
    },
    totalCount() {
      if (!this.searchResults) return 0
      return this.searchResults.totalCount || 0
    },
    totalPages() {
      if (!this.searchResults) return 0
      return this.searchResults.totalPages || 0
    },
    isPaginated: function () {
      if (!this.searchResults) return false
      return this.searchResults.totalPages > 1
    }
  },
  methods: {
    async search() {
      this.loading = true

      const {
        data: { searchUsers }
      } = await this.$apollo.query({
        query: this.$options.query.SearchUsers,
        variables: {
          email: this.searchModel.email,
          page: this.searchModel.currentPage,
          limit: this.searchModel.perPage
        },
        fetchPolicy: 'no-cache'
      })
      this.searchResults = searchUsers

      this.loading = false
    },
    async onSubmit() {
      this.searchModel.currentPage = 1
      await this.search()
    },
    async onPageChange(page) {
      this.searchModel.currentPage = page
      await this.search()
    },
    openChangeEmail(row) {
      this.editingUserId = row.id
      this.$refs.changeEmail.open()
    },
    async changeEmail(newEmail) {
      try {
        this.loading = true
        const input = {
          userId: this.editingUserId,
          email: newEmail
        }

        await this.$apollo.mutate({
          mutation: this.$options.mutation.ChangeUserEmail,
          variables: {
            input
          }
        })

        this.$success(this.$t('change-user-email-success'))

        this.editingUserId = null
        this.$refs.changeEmail.close()
        this.search()
      } finally {
        this.loading = false
      }
    }
  },
  gqlErrors() {
    return {
      EMAIL_ALREADY_ASSIGNED: this.$t('error-email-already-assigned'),
      CHANGE_USER_EMAIL_FAILED: this.$t('error-email-change-failed')
    }
  }
}
</script>

<graphql>
query SearchUsers($email: String!, $page: Int!, $limit: Int!) {
  searchUsers(email: $email, page: $page, limit: $limit) {
    items {
      id
      email
      emailConfirmed
      lastLogin
      profile {
        firstName
        lastName
      }
      business {
        id
        name
      }
    }
    pageNumber
    pageSize
    totalCount
    totalPages
  }
}

mutation ChangeUserEmail($input: ChangeUserEmailInput!) {
  changeUserEmail(input: $input) {
    user {
      id
      email
    }
  }
}
</graphql>
  